import type { SubstrateManifest } from './types';

export const LOCAL_SUBSTRATE_MANIFEST: SubstrateManifest = {
  slug: 'plus-substrate',
  name: 'Plus Substrate',
  version: 'v1-local',
  tier_labels: [
    { id: 'sources', label: 'Sources', color: '#f5b942' },
    { id: 'codex', label: 'Codex', color: '#10b981' },
    { id: 'llm', label: 'LLM Orchestrator', color: '#a78bfa' },
    { id: 'canon', label: 'Canon', color: '#22d3ee' },
    { id: 'governor', label: 'Governor', color: '#e0a158' },
    { id: 'crystallization', label: 'Crystallization', color: '#ec4899' },
    { id: 'sinks', label: 'Sinks', color: '#39ff14' },
    { id: 'approval', label: 'Approval', color: '#ff6b6b' },
    { id: 'triggers', label: 'Triggers', color: '#8a93a1' },
  ],
  primitives: [
    {
      id: 'trigger.manual',
      tier: 'triggers',
      label: 'Manual Trigger',
      description: 'Starts a substrate pipeline when the user explicitly runs it.',
      capability_class: 'trigger',
      provides: [{ id: 'run.start', description: 'Manual run start signal.' }],
    },
    {
      id: 'source.ingest',
      tier: 'sources',
      label: 'Source Ingest',
      description: 'Ingests external material into normalized source documents.',
      capability_class: 'read',
      provides: [{ id: 'source.document_stream', description: 'Normalized documents for downstream primitives.' }],
      parameter_schema: {
        type: 'object',
        properties: {
          source_type: { type: 'string', enum: ['url', 'file', 'rss', 'notion', 'drive'] },
          locator: { type: 'string' },
        },
        required: ['source_type', 'locator'],
      },
      output_shape: { $ref: '#/asset_types/document_stream' },
    },
    {
      id: 'codex.chunk_get',
      tier: 'codex',
      label: 'Codex Chunk Get',
      description: 'Retrieves Codex chunks by query or explicit chunk id.',
      capability_class: 'read',
      requires: [{ id: 'source.document_stream', description: 'Optional source context.' }],
      provides: [{ id: 'codex.chunk_list', description: 'Retrieved Codex chunks.' }],
      parameter_schema: {
        type: 'object',
        properties: {
          limit: { type: 'integer', default: 10, minimum: 1, maximum: 100 },
        },
      },
      input_shape: {
        type: 'object',
        properties: {
          query: { type: 'string' },
          chunk_id: { type: 'string' },
        },
      },
      output_shape: { $ref: '#/asset_types/codex_chunk_set' },
    },
    {
      id: 'llm.complete',
      tier: 'llm',
      label: 'LLM Complete',
      description: 'Generates a completion from prompt and optional context.',
      capability_class: 'compute',
      requires: [{ id: 'prompt.text', description: 'Prompt text to complete against.' }],
      provides: [{ id: 'llm.completion_text', description: 'Generated completion payload.' }],
      parameter_schema: {
        type: 'object',
        properties: {
          model: { type: 'string' },
          temperature: { type: 'number', minimum: 0, maximum: 2, default: 0.7 },
          system_prompt: { type: 'string' },
        },
        required: ['model'],
      },
      input_shape: {
        type: 'object',
        properties: {
          prompt: { type: 'string' },
          context: { type: 'array', items: { type: 'string' } },
        },
        required: ['prompt'],
      },
      output_shape: { $ref: '#/asset_types/completion_text' },
    },
    {
      id: 'approval.gate',
      tier: 'approval',
      label: 'Approval Gate',
      description: 'Pauses a run for human review, modification, approval, or rejection.',
      capability_class: 'approval',
      requires: [{ id: 'approval.review_payload', description: 'Payload requiring human review.' }],
      provides: [{ id: 'approval.review_payload', description: 'Approved or modified payload.' }],
      input_shape: { type: 'object', additionalProperties: true },
      output_shape: { type: 'object', additionalProperties: true },
    },
    {
      id: 'sink.publish',
      tier: 'sinks',
      label: 'Sink Publish',
      description: 'Publishes a substrate artifact to a configured destination.',
      capability_class: 'write',
      requires: [{ id: 'publishable.artifact', description: 'Artifact payload ready for publication.' }],
      provides: [{ id: 'sink.publication_receipt', description: 'Publication metadata and status.' }],
      parameter_schema: {
        type: 'object',
        properties: {
          sink_type: { type: 'string', enum: ['telegram', 'notion', 'email', 'webhook'] },
          destination: { type: 'string' },
        },
        required: ['sink_type', 'destination'],
      },
      input_shape: {
        type: 'object',
        properties: {
          artifact: { type: 'object' },
        },
        required: ['artifact'],
      },
      output_shape: { $ref: '#/asset_types/publication_receipt' },
    },
    {
      id: 'canon.evaluate',
      tier: 'canon',
      label: 'Canon Evaluate',
      description: 'Evaluates an artifact against active taste and quality assertions.',
      capability_class: 'compute',
      requires: [{ id: 'artifact.draft', description: 'Artifact to evaluate.' }],
      provides: [{ id: 'canon.evaluation', description: 'Canon score and rationale.' }],
    },
    {
      id: 'governor.check',
      tier: 'governor',
      label: 'Governor Check',
      description: 'Checks an artifact against active standards and kill switches.',
      capability_class: 'compute',
      requires: [{ id: 'artifact.draft', description: 'Artifact to inspect.' }],
      provides: [{ id: 'governor.verdict', description: 'Pass/fail verdict with violations.' }],
    },
    {
      id: 'crystallization.crystallize',
      tier: 'crystallization',
      label: 'Crystallize',
      description: 'Turns approved session output into memory candidates.',
      capability_class: 'write',
      requires: [{ id: 'artifact.final', description: 'Final artifact or session result.' }],
      provides: [{ id: 'memory.candidate', description: 'Candidate memory ready for persistence.' }],
    },
  ],
  asset_types: [
    { id: 'document_stream', label: 'Document Stream' },
    { id: 'codex_chunk_set', label: 'Codex Chunk Set' },
    { id: 'completion_text', label: 'Completion Text' },
    { id: 'publication_receipt', label: 'Publication Receipt' },
  ],
  default_recipes: [
    {
      id: 'source-to-approved-publish',
      label: 'Source -> Codex -> LLM -> Approval -> Sink',
      primitive_ids: ['source.ingest', 'codex.chunk_get', 'llm.complete', 'approval.gate', 'sink.publish'],
    },
  ],
};
